import React, { useState, useEffect, useRef } from 'react';
import { useSeasonalTheme } from '../../contexts/SeasonalThemeContext';
import { HalloweenTheme, HalloweenHeader, SpiderWeb } from './HalloweenTheme';
import { BlackFridayTheme, BlackFridayHeader } from './BlackFridayTheme';
import { SEASONAL_THEMES } from '../../config/seasonalThemes';

// Main Seasonal Decorations Component
// This component automatically renders the appropriate theme based on the active seasonal theme
export const SeasonalDecorations = ({ location = 'global' }) => {
  const { activeTheme } = useSeasonalTheme();

  if (!activeTheme) return null;

  // Halloween Theme
  if (activeTheme.id === 'halloween') {
    return (
      <HalloweenTheme
        showGhosts={activeTheme.animations.floatingGhosts}
        showBats={true}
        showCursorTrail={activeTheme.animations.cursorTrail}
      />
    );
  }

  // Black Friday Theme
  if (activeTheme.id === 'blackFriday') {
    return <BlackFridayTheme />;
  }

  // Future themes can be added here
  // if (activeTheme.id === 'christmas') return <ChristmasTheme />;
  // if (activeTheme.id === 'melbourneCup') return <MelbourneCupTheme />;

  return null;
};

// Seasonal Header Decoration
export const SeasonalHeaderDecoration = () => {
  const { activeTheme } = useSeasonalTheme();

  if (!activeTheme || !activeTheme.decorations.chatHeader) return null;

  if (activeTheme.id === 'halloween') {
    return <HalloweenHeader />;
  }

  if (activeTheme.id === 'blackFriday') {
    return <BlackFridayHeader />;
  }

  // Future themes
  // if (activeTheme.id === 'christmas') return <ChristmasHeader />;

  return null;
};

// Seasonal Corner Decorations (for cards, modals, etc)
export const SeasonalCornerDecoration = ({ position = 'top-left' }) => {
  const { activeTheme } = useSeasonalTheme();

  if (!activeTheme) return null;

  if (activeTheme.id === 'halloween') {
    return <SpiderWeb position={position} />;
  }

  return null;
};

// Seasonal Badge (small indicator) - Clickable dropdown menu
export const SeasonalBadge = () => {
  const { activeTheme, themeEnabled, toggleTheme } = useSeasonalTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const badges = {
    halloween: '🎃',
    blackFriday: '🎫',
    christmas: '🎄',
    melbourneCup: '🏇',
    newYear: '🎉',
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  if (!activeTheme) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          fontSize: '1.2rem',
          display: 'inline-block',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 4px',
          borderRadius: '4px',
          transition: 'all 0.2s ease',
          opacity: themeEnabled ? 1 : 0.3,
          animation: themeEnabled ? 'seasonalPulse 2s ease-in-out infinite' : 'none',
        }}
        title={`${activeTheme.name} Theme - Click for options`}
        className="hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        {badges[activeTheme.id] || '🎊'}
        <style>{`
          @keyframes seasonalPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.15); }
          }
        `}</style>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div
          className="absolute bottom-full mb-2 right-0 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white">
            <h3 className="font-bold text-sm">Seasonal Themes</h3>
            <p className="text-xs opacity-90 mt-0.5">Festive decorations throughout the year</p>
          </div>

          {/* Active Theme Section */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active Theme</span>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
              <div className="mb-3">
                <h4 className="font-bold text-base text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
                  <span className="text-xl">{badges[activeTheme.id]}</span>
                  {activeTheme.name}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {activeTheme.id === 'halloween' && 'Ghosts, bats, and spooky decorations'}
                  {activeTheme.id === 'blackFriday' && 'Interactive scratch cards with deals & codes'}
                  {activeTheme.id === 'christmas' && 'Snowfall and festive cheer'}
                  {activeTheme.id === 'melbourneCup' && 'The race that stops a nation'}
                  {activeTheme.id === 'newYear' && 'Fireworks and celebrations'}
                </p>
              </div>

              {/* Emoji Effects */}
              <div className="flex flex-wrap gap-2">
                {activeTheme.animations.emojiEffects.map((emoji, i) => (
                  <span key={i} className="text-2xl">{emoji}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Coming Soon Themes */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">Coming Soon</span>
            <div className="space-y-2">
              {Object.values(SEASONAL_THEMES)
                .filter(theme => theme.id !== activeTheme.id)
                .map(theme => (
                  <div key={theme.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{badges[theme.id] || '🎊'}</span>
                      <h5 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{theme.name}</h5>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {theme.id === 'blackFriday' && 'Interactive scratch cards with deals & codes'}
                      {theme.id === 'christmas' && 'Snowfall and festive cheer'}
                      {theme.id === 'melbourneCup' && 'The race that stops a nation'}
                      {theme.id === 'newYear' && 'Fireworks and celebrations'}
                    </p>
                  </div>
                ))}
            </div>
          </div>

          {/* Toggle Button */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Decorations Enabled</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${themeEnabled ? 'text-orange-600' : 'text-gray-400'}`}>
                  {themeEnabled ? 'ON' : 'OFF'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTheme();
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    themeEnabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      themeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Click to {themeEnabled ? 'disable' : 'enable'} seasonal animations
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Seasonal Theme Toggle Button (for settings)
export const SeasonalThemeToggle = () => {
  const { activeTheme, themeEnabled, toggleTheme } = useSeasonalTheme();

  if (!activeTheme) return null;

  return (
    <button
      onClick={toggleTheme}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
      title={`${themeEnabled ? 'Disable' : 'Enable'} ${activeTheme.name} theme`}
    >
      <span>{themeEnabled ? '✅' : '❌'}</span>
      <span className="text-sm">
        {activeTheme.name} Theme
      </span>
    </button>
  );
};
